/**
 * Margin ⇄ coefficient — the browser half of `backend/src/domain/margin.ts`.
 *
 * THE ONE THING TO GET RIGHT: the margin the business uses is MARGIN ON THE SELLING PRICE,
 * not markup on cost.
 *
 *     selling HT  = net purchase price × coefficient
 *     margin      = (selling − cost) / SELLING          ← the denominator is the SELL
 *     coefficient = 1 / (1 − margin)
 *
 *     35 % → 1.5385      65 % → 2.8571      70 % → 3.3333      80 % → 5.0000
 *
 * Markup on cost would give ×1.35 / ×1.65 / ×1.70 / ×1.80. 45 taken as markup is ×1.45, taken
 * as margin it is ×1.8182 — 25 % on every line, and nothing looks wrong on screen. Every field
 * that accepts a margin says "on selling price" in as many words; see `MARGIN_FIELD_LABEL`.
 *
 * Kept byte-for-byte in step with the backend module: the grid computes a preview price here
 * and the invoice computes the real one there, and a browser that rounded differently would
 * show an operator a figure the document then contradicts by a cent.
 */

export const MARGIN_FIELD_LABEL = 'Margin % (on selling price)';

export function roundCoefficient(n: number): number {
  return Number(`${Math.round(Number(`${n}e4`))}e-4`);
}

/** Margin % on the selling price → coefficient. 35 → 1.5385. Null outside [0, 100). */
export function coefficientFromMargin(marginPct: number): number | null {
  if (!Number.isFinite(marginPct) || marginPct < 0 || marginPct >= 100) return null;
  return roundCoefficient(1 / (1 - marginPct / 100));
}

/** Coefficient → margin % on the selling price. 1.5385 → 35. */
export function marginFromCoefficient(coefficient: number): number | null {
  if (!Number.isFinite(coefficient) || coefficient <= 0) return null;
  return Number(`${Math.round(Number(`${((coefficient - 1) / coefficient) * 100}e2`))}e-2`);
}

export const MIN_GRID_COEFFICIENT = 1;
export const MAX_GRID_COEFFICIENT = 100;

export function isSellableCoefficient(n: number): boolean {
  return Number.isFinite(n) && n > MIN_GRID_COEFFICIENT && n <= MAX_GRID_COEFFICIENT;
}

/** Parse a typed figure, French comma included — counter staff type "2,8571". */
export function parseNum(raw: string): number | null {
  const n = parseFloat(raw.trim().replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/** Trailing zeros matter on a coefficient: 1.5385, not 1.5385000 and not 1.54. */
export const fmtCoefficient = (n: number): string => n.toFixed(4);
export const fmtMargin = (n: number): string => `${n.toFixed(2).replace(/\.00$/, '')} %`;
